// src/cockpit4/components/ErrorBoundary.tsx
// ============================================
// 可复用错误边界（局部渲染隔离）
//
// 用途：
//   1. 工作区级隔离：Workspace4 中包裹每个 Workspace，某个 Workspace 渲染异常不会让整页白屏
//   2. 页签级隔离：CEOWorkspace 中包裹每个 Tab 区块，某个子组件异常只影响该区块
//   3. 提供「重试」按钮，无需刷新整个应用即可恢复
//
// 对比 App.tsx 的 AppErrorBoundary：AppErrorBoundary 捕获任何子组件异常会把整页替换为
// 「渲染异常」错误页；本组件只替换其包裹的区域，用户仍可切换工作区 / 页签。
// ============================================

import { Component, type ReactNode } from "react";

interface ErrorBoundaryProps {
  children: ReactNode;
  /** 自定义降级 UI（可选） */
  fallback?: ReactNode;
  /** 错误回调（用于上报 / 埋点，可选） */
  onError?: (error: Error, info: { componentStack: string }) => void;
  /** 变化时重置错误状态（如切换 Tab / Workspace），推荐传入 key */
  resetKey?: string | number;
  /** 降级 UI 标题（默认「区块渲染异常」） */
  title?: string;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export default class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: { componentStack: string }) {
    console.error("[ErrorBoundary] 子组件渲染异常（已隔离）:", error, info);
    this.props.onError?.(error, info);
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps) {
    // resetKey 变化（如切换页签）→ 自动清除错误状态，重新渲染
    if (this.props.resetKey !== prevProps.resetKey && this.state.hasError) {
      this.setState({ hasError: false, error: null });
    }
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    if (this.props.fallback) return this.props.fallback;

    return (
      <div className="p-6 rounded-xl border border-red-500/20 bg-red-500/[0.03] flex flex-col items-center justify-center min-h-[160px] text-center">
        <div className="text-[12px] font-mono text-red-300 mb-2">
          {this.props.title || "区块渲染异常"}
        </div>
        <div className="text-[10px] font-mono text-gray-400 mb-3 break-all max-w-full">
          {this.state.error?.message || String(this.state.error)}
        </div>
        <button
          onClick={this.handleRetry}
          className="px-3 py-1.5 rounded-md bg-red-500/15 border border-red-500/30 text-red-300 text-[11px] font-mono hover:bg-red-500/25 transition-colors cursor-pointer"
        >
          重试
        </button>
      </div>
    );
  }
}
